import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  FileText,
  Loader2,
  MessageSquareWarning,
  Paperclip,
  Send,
  Volume2,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { getConversationSource, getTicketNumber } from '@/features/complaints/conversation-meta'
import {
  complaintsApi,
  type ComplaintSubjectKind,
  type ComplaintAction,
  type ComplaintCategory,
  type ComplaintMessage,
  type ComplaintStatus,
} from '@/services/complaints'
import type { Job } from '@/types'

const STATUS_LABELS: Record<ComplaintStatus, string> = {
  open: 'Open',
  in_review: 'In Review',
  awaiting_customer: 'Awaiting Customer',
  resolved: 'Resolved',
  closed: 'Closed',
}

const CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  service_quality: 'Service Quality',
  worker_conduct: 'Worker Conduct',
  billing: 'Billing',
  scheduling: 'Scheduling',
  damage: 'Damage',
  other: 'Other',
}

const ACTION_LABELS: Record<ComplaintAction, string> = {
  open: 'Opened',
  start_review: 'Start review',
  assign: 'Assign handler',
  await_customer: 'Await customer',
  resolve: 'Resolve',
  close: 'Close',
  reopen: 'Reopen',
}

const MEDIA_ACCEPT = 'image/*,video/*,audio/*,.pdf,.doc,.docx,.txt'

const statusBadgeVariant = (status: ComplaintStatus) => {
  if (status === 'resolved' || status === 'closed') return 'secondary'
  if (status === 'in_review' || status === 'awaiting_customer') return 'outline'
  return 'destructive'
}

const getMessageMediaUrl = (message: ComplaintMessage) =>
  String(message.file_url ?? message.file ?? '').trim() || null

const renderAttachment = (message: ComplaintMessage) => {
  const source = getMessageMediaUrl(message)
  if (!source) {
    return (
      <div className="mt-2 flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span>{message.file_name || 'Attachment'}</span>
      </div>
    )
  }

  if (message.file_type === 'image') {
    return (
      <a href={source} target="_blank" rel="noreferrer" className="mt-2 block max-w-sm">
        <img src={source} alt={message.file_name || 'Complaint image'} className="max-h-56 rounded-lg border object-cover" />
      </a>
    )
  }

  if (message.file_type === 'video') {
    return (
      <video controls className="mt-2 max-h-56 max-w-sm rounded-lg border" src={source}>
        <track kind="captions" />
      </video>
    )
  }

  if (message.file_type === 'audio') {
    return (
      <div className="mt-2 max-w-sm rounded-lg border border-border/60 bg-muted/20 p-3">
        <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Volume2 className="h-4 w-4" />
          {message.file_name || 'Audio message'}
        </div>
        <audio controls className="w-full" src={source} />
      </div>
    )
  }

  return (
    <a
      href={source}
      target="_blank"
      rel="noreferrer"
      className="mt-2 inline-flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm hover:bg-muted/50"
    >
      <Paperclip className="h-4 w-4" />
      {message.file_name || 'Download attachment'}
    </a>
  )
}

interface ComplaintsPanelProps {
  job?: Job
  bookingId?: string | number
  enquiryId?: string | number
  quotationId?: string | number
  invoiceId?: string | number
}

type SubjectContext = {
  kind: ComplaintSubjectKind
  id: string | number
  listParams: {
    job_id?: string | number
    booking_id?: string | number
    enquiry_id?: string | number
    quotation_id?: string | number
    invoice_id?: string | number
    page_size: number
  }
  createParams: {
    job_id?: string | number
    booking_id?: string | number
    enquiry_id?: string | number
    quotation_id?: string | number
    invoice_id?: string | number
  }
}

const resolveSubjectContext = ({
  job,
  bookingId,
  enquiryId,
  quotationId,
  invoiceId,
}: ComplaintsPanelProps): SubjectContext | null => {
  if (job?.id != null) {
    return {
      kind: 'job',
      id: job.id,
      listParams: { job_id: job.id, page_size: 50 },
      createParams: { job_id: job.id },
    }
  }

  if (bookingId != null) {
    return {
      kind: 'booking',
      id: bookingId,
      listParams: { booking_id: bookingId, page_size: 50 },
      createParams: { booking_id: bookingId },
    }
  }

  if (enquiryId != null) {
    return {
      kind: 'enquiry',
      id: enquiryId,
      listParams: { enquiry_id: enquiryId, page_size: 50 },
      createParams: { enquiry_id: enquiryId },
    }
  }

  if (quotationId != null) {
    return {
      kind: 'quotation',
      id: quotationId,
      listParams: { quotation_id: quotationId, page_size: 50 },
      createParams: { quotation_id: quotationId },
    }
  }

  if (invoiceId != null) {
    return {
      kind: 'invoice',
      id: invoiceId,
      listParams: { invoice_id: invoiceId, page_size: 50 },
      createParams: { invoice_id: invoiceId },
    }
  }

  return null
}

export function ComplaintsPanel({ job, bookingId, enquiryId, quotationId, invoiceId }: ComplaintsPanelProps) {
  const subjectContext = resolveSubjectContext({ job, bookingId, enquiryId, quotationId, invoiceId })
  const contextKey = subjectContext?.id ?? null
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedComplaintId, setSelectedComplaintId] = useState<string | number | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newSubject, setNewSubject] = useState('')
  const [newCategory, setNewCategory] = useState<ComplaintCategory>('other')
  const [newDescription, setNewDescription] = useState('')
  const [replyBody, setReplyBody] = useState('')
  const [replyInternal, setReplyInternal] = useState(false)
  const [actionNote, setActionNote] = useState('')
  const [resolutionNote, setResolutionNote] = useState('')
  const [assignUserId, setAssignUserId] = useState('')
  const [pendingAction, setPendingAction] = useState<ComplaintAction | null>(null)

  if (subjectContext == null || contextKey == null) {
    return (
      <Card>
        <CardContent className="py-8 text-sm text-muted-foreground">
          A valid job, booking, enquiry, quotation, or invoice is required to manage complaints.
        </CardContent>
      </Card>
    )
  }

  const complaintsQuery = useQuery({
    queryKey: ['complaints', contextKey, subjectContext.kind],
    queryFn: () => complaintsApi.list(subjectContext.listParams),
    enabled: contextKey != null,
  })

  const complaints = complaintsQuery.data?.results ?? []
  const selectedComplaint = useMemo(
    () => complaints.find((item) => String(item.id) === String(selectedComplaintId)) ?? null,
    [complaints, selectedComplaintId],
  )

  const messagesQuery = useQuery({
    queryKey: ['complaint-messages', selectedComplaintId],
    queryFn: () => complaintsApi.listMessages(selectedComplaintId!),
    enabled: selectedComplaintId != null,
  })

  const followUpsQuery = useQuery({
    queryKey: ['complaint-follow-ups', selectedComplaintId],
    queryFn: () => complaintsApi.listFollowUps(selectedComplaintId!),
    enabled: selectedComplaintId != null,
  })

  const invalidateComplaints = () => {
    queryClient.invalidateQueries({ queryKey: ['complaints', contextKey] })
    if (selectedComplaintId != null) {
      queryClient.invalidateQueries({ queryKey: ['complaint-messages', selectedComplaintId] })
      queryClient.invalidateQueries({ queryKey: ['complaint-follow-ups', selectedComplaintId] })
    }
  }

  const createMutation = useMutation({
    mutationFn: complaintsApi.create,
    onSuccess: (created) => {
      toast.success(`Ticket ${getTicketNumber(created)} created`)
      setShowCreateForm(false)
      setNewSubject('')
      setNewDescription('')
      setSelectedComplaintId(created.id)
      invalidateComplaints()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const sendMutation = useMutation({
    mutationFn: complaintsApi.sendMessage,
    onSuccess: () => {
      setReplyBody('')
      invalidateComplaints()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const uploadMutation = useMutation({
    mutationFn: complaintsApi.uploadMessage,
    onSuccess: () => {
      setReplyBody('')
      invalidateComplaints()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const actionMutation = useMutation({
    mutationFn: (payload: { id: string | number; action: ComplaintAction }) =>
      complaintsApi.applyAction(payload.id, {
        action: payload.action,
        note: actionNote.trim() || undefined,
        resolution_note: payload.action === 'resolve' ? resolutionNote.trim() : undefined,
        assigned_to: payload.action === 'assign' ? assignUserId.trim() || null : undefined,
      }),
    onSuccess: (updated) => {
      toast.success(`Ticket ${getTicketNumber(updated)} updated`)
      setActionNote('')
      setResolutionNote('')
      setAssignUserId('')
      setPendingAction(null)
      invalidateComplaints()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const handleAction = (action: ComplaintAction) => {
    if (!selectedComplaintId) return
    if (action === 'resolve' && !resolutionNote.trim() && !actionNote.trim()) {
      setPendingAction(action)
      return
    }
    if (action === 'assign' && !assignUserId.trim()) {
      setPendingAction(action)
      return
    }
    actionMutation.mutate({ id: selectedComplaintId, action })
  }

  const handleCreate = () => {
    const subject = newSubject.trim()
    if (!subject) {
      toast.error('Subject is required')
      return
    }
    createMutation.mutate({
      ...subjectContext.createParams,
      subject,
      category: newCategory,
      description: newDescription.trim(),
      body: newDescription.trim() || subject,
    })
  }

  const handleSend = () => {
    if (!selectedComplaintId) return
    const body = replyBody.trim()
    if (!body) {
      toast.error('Enter a message')
      return
    }
    sendMutation.mutate({
      complaint_id: selectedComplaintId,
      body,
      is_internal: replyInternal,
    })
  }

  const handleUpload = (file: File | undefined) => {
    if (!selectedComplaintId || !file) return
    uploadMutation.mutate({
      complaint_id: selectedComplaintId,
      file,
      body: replyBody.trim() || undefined,
      is_internal: replyInternal,
    })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const messages = messagesQuery.data?.results ?? []
  const isSending = sendMutation.isPending || uploadMutation.isPending

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Support tickets</CardTitle>
              <CardDescription>Each complaint is tracked as a numbered ticket with status and follow-ups</CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowCreateForm((value) => !value)}>
              New
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showCreateForm ? (
            <div className="space-y-3 rounded-xl border border-border/70 p-3">
              <div className="space-y-2">
                <Label htmlFor="complaint-subject">Subject</Label>
                <Input
                  id="complaint-subject"
                  value={newSubject}
                  onChange={(event) => setNewSubject(event.target.value)}
                  placeholder="Brief summary"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newCategory} onValueChange={(value) => setNewCategory(value as ComplaintCategory)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="complaint-description">Initial message</Label>
                <Textarea
                  id="complaint-description"
                  rows={4}
                  value={newDescription}
                  onChange={(event) => setNewDescription(event.target.value)}
                  placeholder="Describe the issue in detail..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                <Button size="sm" disabled={createMutation.isPending} onClick={handleCreate}>
                  {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create
                </Button>
              </div>
            </div>
          ) : null}

          {complaintsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : complaints.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon"><MessageSquareWarning /></EmptyMedia>
                <EmptyTitle>No complaints</EmptyTitle>
                <EmptyDescription>
                  Complaints raised from the customer app, worker app, or portal will appear here.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="space-y-2">
              {complaints.map((complaint) => (
                <button
                  key={complaint.id}
                  type="button"
                  onClick={() => {
                    setSelectedComplaintId(complaint.id)
                    setPendingAction(null)
                    setActionNote('')
                    setResolutionNote('')
                    setAssignUserId('')
                  }}
                  className={`w-full rounded-xl border px-3 py-3 text-left transition-colors ${
                    String(selectedComplaintId) === String(complaint.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-border/70 hover:bg-muted/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-foreground">{complaint.subject}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {getTicketNumber(complaint)}
                        {' · '}
                        {CATEGORY_LABELS[complaint.category] ?? complaint.category}
                        {complaint.assigned_to_name ? ` · ${complaint.assigned_to_name}` : ''}
                      </p>
                    </div>
                    <Badge variant={statusBadgeVariant(complaint.status)}>
                      {complaint.status_label || STATUS_LABELS[complaint.status]}
                    </Badge>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{complaint.description}</p>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    {complaint.message_count ?? 0} messages
                    {complaint.last_message_at ? ` · ${formatDate(complaint.last_message_at)}` : ''}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="min-h-[520px]">
        <CardHeader>
          {selectedComplaint ? (
            <div className="space-y-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{getTicketNumber(selectedComplaint)}</Badge>
                    <Badge variant={statusBadgeVariant(selectedComplaint.status)}>
                      {selectedComplaint.status_label || STATUS_LABELS[selectedComplaint.status]}
                    </Badge>
                    <Badge variant="secondary">{getConversationSource(selectedComplaint)}</Badge>
                  </div>
                  <CardTitle className="text-base">{selectedComplaint.subject}</CardTitle>
                  <CardDescription>
                    Raised by {selectedComplaint.raised_by_name || 'Unknown'}
                    {' · '}
                    {selectedComplaint.complainant_role}
                    {selectedComplaint.assigned_to_name ? ` · Assigned to ${selectedComplaint.assigned_to_name}` : ''}
                  </CardDescription>
                  <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                    <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                      <p className="font-medium text-foreground">Customer email</p>
                      <p className="mt-1 break-all">{selectedComplaint.customer_email || 'Not available'}</p>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
                      <p className="font-medium text-foreground">Reply address</p>
                      <p className="mt-1 break-all">{selectedComplaint.email_reply_address || 'Not configured yet'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {(selectedComplaint.allowed_actions?.length ?? 0) > 0 ? (
                <div className="rounded-xl border border-border/70 p-3">
                  <p className="mb-2 text-sm font-medium text-foreground">Follow-up actions</p>
                  <div className="flex flex-wrap gap-2">
                    {(selectedComplaint.allowed_actions ?? []).map((action) => (
                      <Button
                        key={action}
                        type="button"
                        size="sm"
                        variant={pendingAction === action ? 'default' : 'outline'}
                        disabled={actionMutation.isPending}
                        onClick={() => {
                          if (action === 'resolve' || action === 'assign') {
                            setPendingAction((current) => (current === action ? null : action))
                            return
                          }
                          actionMutation.mutate({ id: selectedComplaint.id, action })
                        }}
                      >
                        {ACTION_LABELS[action]}
                      </Button>
                    ))}
                  </div>

                  {pendingAction === 'assign' ? (
                    <div className="mt-3 space-y-2">
                      <Label htmlFor="assign-user-id">Assign to user ID</Label>
                      <Input
                        id="assign-user-id"
                        value={assignUserId}
                        onChange={(event) => setAssignUserId(event.target.value)}
                        placeholder="Staff user ID"
                      />
                    </div>
                  ) : null}

                  {pendingAction === 'resolve' ? (
                    <div className="mt-3 space-y-2">
                      <Label htmlFor="resolution-note">Resolution note</Label>
                      <Textarea
                        id="resolution-note"
                        rows={3}
                        value={resolutionNote}
                        onChange={(event) => setResolutionNote(event.target.value)}
                        placeholder="Explain how the ticket was resolved..."
                      />
                    </div>
                  ) : null}

                  {pendingAction ? (
                    <div className="mt-3 space-y-2">
                      <Label htmlFor="action-note">Action note (optional)</Label>
                      <Textarea
                        id="action-note"
                        rows={2}
                        value={actionNote}
                        onChange={(event) => setActionNote(event.target.value)}
                        placeholder="Add context for this follow-up..."
                      />
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPendingAction(null)}>Cancel</Button>
                        <Button
                          size="sm"
                          disabled={actionMutation.isPending}
                          onClick={() => handleAction(pendingAction)}
                        >
                          Apply {ACTION_LABELS[pendingAction]}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {selectedComplaint.resolution_note ? (
                <div className="rounded-xl border border-border/70 bg-muted/20 p-3 text-sm">
                  <p className="font-medium text-foreground">Resolution</p>
                  <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{selectedComplaint.resolution_note}</p>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              <CardTitle className="text-base">Conversation</CardTitle>
              <CardDescription>Select a complaint to view the thread</CardDescription>
            </>
          )}
        </CardHeader>
        <CardContent className="flex h-full flex-col">
          {!selectedComplaint ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon"><MessageSquareWarning /></EmptyMedia>
                <EmptyTitle>Select a complaint</EmptyTitle>
                <EmptyDescription>
                  Choose a complaint from the list to read and reply to the conversation.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <>
              {(followUpsQuery.data?.results?.length ?? 0) > 0 ? (
                <div className="mb-4 rounded-xl border border-border/60 bg-background p-3">
                  <p className="mb-2 text-sm font-medium text-foreground">Follow-up timeline</p>
                  <div className="space-y-2">
                    {followUpsQuery.data?.results.map((followUp) => (
                      <div key={followUp.id} className="rounded-lg border border-border/50 px-3 py-2 text-sm">
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground">{followUp.action_label || ACTION_LABELS[followUp.action]}</span>
                          {followUp.from_status_label && followUp.to_status_label ? (
                            <span>{followUp.from_status_label} → {followUp.to_status_label}</span>
                          ) : followUp.to_status_label ? (
                            <span>{followUp.to_status_label}</span>
                          ) : null}
                          {followUp.created_at ? <span>{formatDate(followUp.created_at)}</span> : null}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {followUp.performed_by_name || 'Staff'}
                          {followUp.assigned_to_name ? ` · assigned ${followUp.assigned_to_name}` : ''}
                        </p>
                        {followUp.note ? (
                          <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{followUp.note}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="mb-4 max-h-[420px] flex-1 space-y-3 overflow-y-auto rounded-xl border border-border/60 bg-muted/10 p-4">
                {messagesQuery.isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-20 w-3/4" />
                    <Skeleton className="ml-auto h-20 w-3/4" />
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No messages yet.</p>
                ) : (
                  messages.map((message) => {
                    const isStaff = message.sender_role === 'admin' || message.sender_role === 'staff'
                    const isSystem = message.message_type === 'system'
                    return (
                      <div
                        key={message.id}
                        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                          isSystem
                            ? 'mx-auto bg-muted/50 text-center text-sm text-muted-foreground'
                            : isStaff
                              ? 'ml-auto bg-primary text-primary-foreground'
                              : 'bg-background border border-border/70'
                        }`}
                      >
                        {!isSystem ? (
                          <div className={`mb-1 flex flex-wrap items-center gap-2 text-xs ${isStaff ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                            <span className="font-medium">{message.sender_name || 'User'}</span>
                            <span>{message.sender_role}</span>
                            {message.source ? <Badge variant="outline" className={isStaff ? 'border-primary-foreground/40 text-primary-foreground' : ''}>{message.source}</Badge> : null}
                            {message.is_internal ? <Badge variant="outline">Internal</Badge> : null}
                            {message.created_at ? <span>{formatDate(message.created_at)}</span> : null}
                          </div>
                        ) : null}
                        {message.body ? (
                          <p className={`whitespace-pre-wrap text-sm ${
                            isSystem
                              ? 'text-muted-foreground'
                              : isStaff
                                ? 'text-primary-foreground'
                                : 'text-foreground'
                          }`}>
                            {message.body}
                          </p>
                        ) : null}
                        {message.message_type === 'attachment' ? renderAttachment(message) : null}
                      </div>
                    )
                  })
                )}
              </div>

              <div className="space-y-3 border-t border-border/60 pt-4">
                <Textarea
                  rows={3}
                  value={replyBody}
                  onChange={(event) => setReplyBody(event.target.value)}
                  placeholder={replyInternal ? 'Write an internal note...' : 'Write an email reply...'}
                />
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={replyInternal}
                      onChange={(event) => setReplyInternal(event.target.checked)}
                    />
                    Internal staff note
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={MEDIA_ACCEPT}
                      className="hidden"
                      onChange={(event) => handleUpload(event.target.files?.[0])}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isSending}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Paperclip className="mr-2 h-4 w-4" />
                      Attach
                    </Button>
                    <Button size="sm" disabled={isSending} onClick={handleSend}>
                      {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                      Send
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Supports text, images, video, audio, and documents. External replies are delivered through Postal; internal notes stay inside the portal.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
